import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaIdCard, FaUsers, FaCheckCircle, FaTimesCircle, FaClock, FaPlus } from 'react-icons/fa';
import { validateOrganizationToken } from '../../../utils/urlSecurity';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const OrgLicenses = () => {
  const { orgId: secureToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [licenseUsage, setLicenseUsage] = useState(null);
  const [organizationName, setOrganizationName] = useState('');
  const [organizationId, setOrganizationId] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestedLicenses, setRequestedLicenses] = useState('');
  const [notes, setNotes] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    const initializePage = async () => {
      try {
        const orgName = location.state?.organizationName || 'Organization';
        setOrganizationName(orgName);

        const tokenValidation = validateOrganizationToken(secureToken);
        if (!tokenValidation.valid) {
          if (tokenValidation.expired) {
            toast.error('Navigation token has expired. Please navigate from the organizations page again.');
          } else {
            toast.error('Invalid navigation token. Please navigate from the organizations page.');
          }
          navigate('/admin/organizations');
          return;
        }
        const orgId = tokenValidation.orgId;
        setOrganizationId(orgId);

        await fetchLicenseData(orgId);
      } catch (error) {
        console.error('Error initializing page:', error);
        toast.error('Failed to load organization data');
        navigate('/admin/organizations');
      }
    };

    initializePage();
  }, [secureToken, location.state, navigate]);

  const fetchLicenseData = async (orgId) => {
    try {
      setLoading(true);
      
      const requestsResponse = await api.licenses.getOrganizationRequests(orgId);
      const requestsData = requestsResponse?.data?.data || requestsResponse?.data || [];
      setLicenseRequests(Array.isArray(requestsData) ? requestsData : []);

      const usageResponse = await api.licenses.getOrganizationUsage(orgId);
      const usageData = usageResponse?.data?.data || usageResponse?.data || null;
      setLicenseUsage(usageData);

    } catch (error) {
      console.error('Error fetching license data:', error);
      toast.error('Failed to load license data');
      setLicenseRequests([]);
      setLicenseUsage(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/admin/organizations');
  };

  const handleShowRequestModal = () => {
    setRequestedLicenses('');
    setNotes('');
    setFormErrors({});
    setShowRequestModal(true);
  };

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setRequestedLicenses('');
    setNotes('');
    setFormErrors({});
  };

  const validateRequestForm = () => {
    const errors = {};
    const count = parseInt(requestedLicenses, 10);
    if (!requestedLicenses || Number.isNaN(count) || count <= 0) {
      errors.requestedLicenses = 'Requested licenses must be a positive number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateRequest = async () => {
    if (!validateRequestForm() || !organizationId) return;

    setModalLoading(true);
    try {
      const response = await api.licenses.createRequest(
        organizationId,
        parseInt(requestedLicenses, 10),
        notes.trim() || null
      );
      const autoApproved = response.data?.autoApproved;
      toast.success(
        autoApproved
          ? 'License request created and approved successfully!'
          : 'License request submitted successfully!'
      );
      handleCloseRequestModal();
      await fetchLicenseData(organizationId);
    } catch (error) {
      console.error('Error creating license request:', error);
      toast.error('Failed to create license request: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 w-fit">
            <FaCheckCircle /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 w-fit">
            <FaTimesCircle /> Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 w-fit">
            <FaClock /> Pending
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-0">
            {organizationName}'s Licenses
          </h5>
        </div>
        <button
          onClick={handleShowRequestModal}
          className="flex items-center gap-2 px-4 py-2 border border-purple-600 bg-white text-purple-600 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 text-sm"
        >
          <FaPlus className="w-4 h-4" />
          Request Licenses
        </button>
      </div>

      {/* License Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 text-center border border-purple-100">
          <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            {licenseUsage ? licenseUsage.total_licenses : 0}
          </div>
          <p className="text-sm font-medium text-gray-800 mt-2">Total Licenses Approved</p>
        </div>
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 text-center border border-purple-100">
          <div className="text-4xl font-extrabold text-orange-600">
            {licenseUsage ? licenseUsage.used_licenses : 0}
          </div>
          <p className="text-sm font-medium text-gray-800 mt-2">Organization Users</p>
        </div>
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 text-center border border-purple-100">
          <div className={`text-4xl font-extrabold ${licenseUsage && licenseUsage.remaining_licenses > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {licenseUsage ? licenseUsage.remaining_licenses : 0}
          </div>
          <p className="text-sm font-medium text-gray-800 mt-2">Available Licenses</p>
        </div>
      </div>

      {/* License Requests Table */}
      {licenseRequests.length === 0 ? (
        <div className="text-center py-10 rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur shadow-md border border-purple-100">
          <p className="text-lg font-medium text-purple-700">
            No license requests found for this organization.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left font-bold">Request ID</th>
                <th className="py-2 px-3 text-left font-bold">Requested Date</th>
                <th className="py-2 px-3 text-left font-bold">Number of Licenses</th>
                <th className="py-2 px-3 text-left font-bold">Status</th>
                <th className="py-2 px-3 text-left font-bold">Approved/Rejected By</th>
                <th className="py-2 px-3 text-left font-bold">Action Date</th>
                <th className="py-2 px-3 text-left font-bold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {licenseRequests.map((request) => (
                <tr key={request.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-2 px-3">#{request.id}</td>
                  <td className="py-2 px-3 text-gray-800">{formatDate(request.createdAt)}</td>
                  <td className="py-2 px-3">
                    <div className="text-gray-800">{request.requested_licenses}</div>
                    {request.status === 'approved' && request.approved_licenses !== request.requested_licenses && (
                      <small className="text-gray-500">Approved: {request.approved_licenses}</small>
                    )}
                  </td>
                  <td className="py-2 px-3 text-left">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="py-2 px-3">
                    {request.approver ? (
                      <div>
                        <div className="text-gray-800">
                          {`${request.approver.first_name || ''} ${request.approver.last_name || ''}`.trim() || request.approver.email}
                        </div>
                        <div className="text-xs text-gray-500">{request.approver.email}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-gray-800">
                    {request.approved_at ? formatDate(request.approved_at) : '-'}
                  </td>
                  <td className="py-2 px-3 max-w-xs">
                    {request.notes || request.rejection_reason ? (
                      <div className="text-sm">
                        {request.notes && (
                          <div className="text-gray-800 mb-1">{request.notes}</div>
                        )}
                        {request.rejection_reason && (
                          <div className="text-red-600 font-medium">
                            Rejection: {request.rejection_reason}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal show={showRequestModal} onHide={handleCloseRequestModal} centered backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
          <Modal.Title className="font-bold text-lg text-white">Request Licenses</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-4">
          <p className="text-sm text-gray-600 mb-3">
            As a super admin, license requests raised here are automatically approved and added to this organization.
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="font-semibold text-gray-700 text-sm">
              Number of Licenses <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={requestedLicenses}
              onChange={(e) => {
                setRequestedLicenses(e.target.value);
                if (formErrors.requestedLicenses) {
                  setFormErrors((prev) => ({ ...prev, requestedLicenses: '' }));
                }
              }}
              isInvalid={!!formErrors.requestedLicenses}
              placeholder="Enter number of licenses"
              className="rounded-lg border-purple-200"
            />
            <Form.Control.Feedback type="invalid">{formErrors.requestedLicenses}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label className="font-semibold text-gray-700 text-sm">Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes for this license allocation"
              className="rounded-lg border-purple-200"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCloseRequestModal}
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 text-gray-700 border-2 border-gray-300 font-semibold rounded-lg p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleCreateRequest}
                disabled={modalLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 font-semibold rounded-lg p-2"
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Submitting...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrgLicenses;